/**
 * @fileoverview Game configuration constants.
 * Pure configuration data - all game settings, dimensions, colors, and behaviors.
 */

import { BaseTheme } from '../themes/abstract/BaseTheme.js';
import { ResponsiveUtils } from '../utilities/ResponsiveUtils.js';
import { menuStrings } from '../i18n/menu.js';

/**
 * Color pair for shapes.
 */
export interface ColorPair {
    fill: string;
    stroke: string;
}

/**
 * Audio configuration.
 */
export interface AudioConfig {
    ENABLED: boolean;
    BACKGROUND_MUSIC_PATH: string;
    VOLUME: number;
    LOWER_VOLUME: number;
    LOOP: boolean;
    START_MUTED: boolean;
}

/**
 * Mute button UI configuration.
 */
export interface MuteButtonConfig {
    ENABLED: boolean;
    SIZE_MIN: number;
    SIZE_MAX: number;
    SIZE_SCALE: number;
    PADDING: number;
    POSITION: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    BACKGROUND_COLOR: string;
    STROKE_COLOR: string;
    STROKE_WIDTH: number;
    ICON_UNMUTED: string;
    ICON_MUTED: string;
}

/**
 * UI configuration.
 */
export interface UIConfig {
    MUTE_BUTTON: MuteButtonConfig;
}

/**
 * Game configuration class.
 * Centralized configuration for all game settings.
 */
class GameConfig {
    // Canvas dimensions (responsive, mutable)
    WIDTH: number;
    HEIGHT: number;
    NUM_PAIRS: number;
    
    // Shape properties (responsive, mutable for SHAPE_SIZE)
    SHAPE_SIZE: number;
    readonly CORNER_RADIUS: number = 15;
    readonly TOLERANCE: number = 70; // High tolerance for toddlers (in pixels)
    
    // Animation durations (in seconds) - minimized for old device performance
    readonly DRAG_SCALE_DURATION: number = 0.03;
    readonly SNAP_DURATION: number = 0.15;
    readonly SUCCESS_ANIMATION_DURATION: number = 0.1;
    readonly BOUNCE_BACK_DURATION: number = 0.2;
    readonly RESET_DELAY: number = 2500; // milliseconds
    readonly SUCCESS_MESSAGE_DURATION: number = 800; // milliseconds
    
    // Scale values - 10% growth on drag for clear visual feedback
    readonly DRAG_SCALE: number = 1.1;
    readonly SUCCESS_SCALE: number = 1.1;
    
    // Colors - array for cycling through different colors
    readonly DRAGGABLE_COLORS: ColorPair[] = [
        { fill: '#FF6B35', stroke: '#FF4500' }, // Orange
        { fill: '#4ECDC4', stroke: '#45B7AF' }, // Turquoise
        { fill: '#FFD93D', stroke: '#F7C22E' }, // Yellow
        { fill: '#FF6B9D', stroke: '#FF4584' }, // Pink
        { fill: '#A8E6CF', stroke: '#7FD6B0' }, // Mint green
        { fill: '#957DAD', stroke: '#7B6699' }  // Purple
    ];
    readonly SUCCESS_COLOR: string = '#4CAF50';
    readonly TARGET_FILL: string = '#e0e0e0';
    readonly TARGET_STROKE: string = '#999';
    readonly TITLE_COLOR: string = '#333';
    
    // Text (responsive, mutable for font sizes)
    readonly TITLE_TEXT: string = 'Drag the square! 🎯';
    TITLE_FONT_SIZE: number;
    TITLE_Y_POSITION: number;
    readonly SUCCESS_EMOJI: string = '🎉';
    get FEEDBACK_MESSAGES(): string[] { return menuStrings.feedbackMessages; }
    FEEDBACK_FONT_SIZE: number;
    FEEDBACK_Y_POSITION: number;
    NUMBER_FONT_SIZE: number;
    
    // Audio settings
    readonly AUDIO: AudioConfig = {
        ENABLED: true,
        BACKGROUND_MUSIC_PATH: '/sounds/main_tune.mp3',
        VOLUME: 0.1, // 0.0 to 1.0
        LOWER_VOLUME: 0.05, // 0.0 to 1.0
        LOOP: true,
        START_MUTED: true // Start muted to allow autoplay, unmute on interaction
    };
    
    // UI settings
    readonly UI: UIConfig = {
        MUTE_BUTTON: {
            ENABLED: true,
            SIZE_MIN: 40,
            SIZE_MAX: 60,
            SIZE_SCALE: 10, // HEIGHT / this value
            PADDING: 20,
            POSITION: 'top-right', // 'top-right', 'top-left', 'bottom-right', 'bottom-left'
            BACKGROUND_COLOR: 'rgba(0, 0, 0, 0.3)',
            STROKE_COLOR: 'white',
            STROKE_WIDTH: 2,
            ICON_UNMUTED: '🔊',
            ICON_MUTED: '🔇'
        }
    };

    constructor() {
        // Initialize with default dimensions (no theme known yet)
        this.updateDimensions(null);
    }

    /**
     * Update dimensions based on current window size.
     */
    updateDimensions(theme: BaseTheme | null): void {
        const newDimensions = ResponsiveUtils.getResponsiveDimensions();
        this.WIDTH = newDimensions.width;
        this.HEIGHT = newDimensions.height;
        this.SHAPE_SIZE = theme ? theme.getShapeSize(newDimensions.height, newDimensions.width) : 80;
        this.TITLE_FONT_SIZE = ResponsiveUtils.getResponsiveTitleFontSize(newDimensions.height);
        this.TITLE_Y_POSITION = newDimensions.height * 0.08;
        this.FEEDBACK_FONT_SIZE = ResponsiveUtils.getResponsiveFeedbackFontSize(newDimensions.height);
        this.FEEDBACK_Y_POSITION = newDimensions.height * 0.6;
        this.NUMBER_FONT_SIZE = ResponsiveUtils.getResponsiveNumberFontSize(newDimensions.height);
    }
}

/**
 * Singleton instance of game configuration.
 */
export const CONFIG = new GameConfig();


